import * as React from 'react';
import { createContext, useCallback, useContext, useMemo } from 'react';

import { Checkmark, WarningFilled } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';

import { typographyVariants } from '../../lib/typography.jsx';
import { computeStepState } from './Stepper.helpers.jsx';
import type {
  StepItemContextValue,
  StepperContextValue,
} from './Stepper.types.js';

// Contexts
const StepperContext = createContext<StepperContextValue | undefined>(
  undefined,
);
const StepItemContext = createContext<StepItemContextValue | undefined>(
  undefined,
);

const useStepper = () => {
  const context = useContext(StepperContext);
  if (!context) {
    throw new Error('useStepper must be used within a Stepper');
  }
  return context;
};

const useStepItem = () => {
  const context = useContext(StepItemContext);
  if (!context) {
    throw new Error('useStepItem must be used within a StepperItem');
  }
  return context;
};

// Components
interface StepperProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultValue?: number;
  value?: number;
  onValueChange?: (value: number) => void;
  completedStep?: { [k: number]: boolean };
  isStepFailed?: (step: number) => boolean;
}

const StepperRoot = ({
  defaultValue = 0,
  value,
  onValueChange,
  completedStep,
  isStepFailed,
  className,
  ...props
}: React.ComponentProps<'div'> & StepperProps) => {
  const [activeStep, setInternalStep] = React.useState(defaultValue ?? value);
  const [completedSteps, setCompletedSteps] = React.useState<{
    [k: number]: boolean;
  }>(completedStep ?? {});
  const [errorStep, setErrorStep] = React.useState<number | undefined>(
    undefined,
  );

  const setActiveStep = React.useCallback(
    (step: number) => {
      if (value === undefined) {
        setInternalStep(step);
      }
      onValueChange?.(step);
    },
    [value, onValueChange],
  );
  const setCompletedStep = React.useCallback(
    (step: number, completed: boolean) => {
      setCompletedSteps((prev) => ({ ...prev, [step]: completed }));
    },
    [setCompletedSteps],
  );

  const handleStepFailed = useCallback(
    (step: number) => {
      if (isStepFailed?.(step)) {
        setErrorStep(step);
      }
    },
    [isStepFailed, setErrorStep],
  );

  return (
    <StepperContext.Provider
      value={useMemo(
        () => ({
          activeStep: value ?? activeStep,
          setActiveStep,
          completedStep: completedSteps,
          setCompletedStep,
          errorStep,
          setErrorStep: handleStepFailed,
        }),
        [
          value,
          activeStep,
          setActiveStep,
          completedSteps,
          setCompletedStep,
          errorStep,
          handleStepFailed,
        ],
      )}
    >
      <div
        className={cn('group/stepper flex w-full gap-2', className)}
        {...props}
      />
    </StepperContext.Provider>
  );
};

StepperRoot.displayName = 'StepperRoot';

// StepperItem
interface StepperItemProps extends React.HTMLAttributes<HTMLDivElement> {
  step: number;
  completed?: boolean;
  error?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

const StepperItem = ({
  step,
  completed = false,
  error = false,
  disabled = false,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & StepperItemProps) => {
  const { activeStep } = useStepper();

  const state = computeStepState(completed, activeStep === step, error);

  return (
    <StepItemContext.Provider
      value={useMemo(
        () => ({
          step,
          state,
          isDisabled: disabled,
        }),
        [step, state, disabled],
      )}
    >
      <div
        className={cn(
          'group/step grid min-w-0 flex-1 grid-cols-[auto_minmax(0,auto)_1fr] items-center justify-start gap-2 last:flex-none last:grid-cols-[auto_minmax(0,auto)]',
          className,
        )}
        data-state={disabled ? 'disabled' : state}
        aria-disabled={disabled}
        {...props}
      >
        {children}
      </div>
    </StepItemContext.Provider>
  );
};
StepperItem.displayName = 'StepperItem';

// StepperTrigger
interface StepperTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const StepperTrigger = ({
  asChild = false,
  className,
  children,
  ...props
}: React.ComponentProps<'button'> & StepperTriggerProps) => {
  const { isDisabled } = useStepItem();
  if (asChild) {
    return <div className={className}>{children}</div>;
  }

  return (
    <button
      type="button"
      className={cn(
        'inline-flex cursor-pointer items-center gap-3 rounded-full focus-visible:outline-2 focus-visible:outline-action-focusBorder disabled:pointer-events-none',
        className,
      )}
      disabled={isDisabled}
      {...props}
    >
      {children}
    </button>
  );
};
StepperTrigger.displayName = 'StepperTrigger';

// StepperIndicator
const StepperIndicator = ({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) => {
  const { state, step, isDisabled } = useStepItem();

  return (
    <div
      className={cn(
        'relative flex size-8 shrink-0 items-center justify-center rounded-full data-[state=active]:border-3 data-[state=active]:border-tertiary-main data-[state=active]:bg-ui-primary data-[state=active]:text-text-primary data-[state=completed]:bg-tertiary-main data-[state=completed]:fill-greyscale-white data-[state=disabled]:bg-action-disabledBackground data-[state=disabled]:text-action-disabled01 data-[state=error]:bg-error-main data-[state=error]:fill-greyscale-white data-[state=inactive]:bg-action-disabledBackground data-[state=inactive]:text-text-disabled02',
        className,
      )}
      aria-disabled={isDisabled}
      data-state={isDisabled ? 'disabled' : state}
      {...props}
    >
      <p
        className={cn(
          typographyVariants({
            variant: 'h6',
          }),
          'transition-all group-data-[state=completed]/step:scale-0 group-data-[state=completed]/step:opacity-0 group-data-[state=error]/step:scale-0 group-data-[state=error]/step:opacity-0',
        )}
      >
        {step + 1}
      </p>
      <Checkmark
        className="absolute size-5 scale-0 opacity-0 transition-all group-data-[state=completed]/step:scale-100 group-data-[state=completed]/step:opacity-100"
        aria-hidden="true"
      />
      <WarningFilled
        className="absolute size-5 scale-0 opacity-0 transition-all group-data-[state=error]/step:scale-100 group-data-[state=error]/step:opacity-100"
        aria-hidden="true"
      />
    </div>
  );
};
StepperIndicator.displayName = 'StepperIndicator';

// StepperTitle
const StepperTitle = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => {
  const { state, isDisabled } = useStepItem();

  return (
    <span
      data-state={isDisabled ? 'disabled' : state}
      className={cn(
        typographyVariants({
          variant: 'bodyShort02',
        }),
        'text-wrap text-tertiary-contrastText data-[state=disabled]:text-text-disabled data-[state=error]:text-text-error',
        className,
      )}
      {...props}
    >
      {props.children}
    </span>
  );
};
StepperTitle.displayName = 'StepperTitle';

// StepperDescription
const StepperDescription = ({
  className,
  ...props
}: React.ComponentProps<'p'>) => {
  const { state, isDisabled } = useStepItem();

  return (
    <p
      data-state={isDisabled ? 'disabled' : state}
      className={cn(
        typographyVariants({
          variant: 'caption2',
        }),
        'text-wrap text-text-primary data-[state=disabled]:text-text-disabled data-[state=error]:text-text-error',
        className,
      )}
      {...props}
    />
  );
};
StepperDescription.displayName = 'StepperDescription';

export {
  StepperRoot,
  StepperDescription,
  StepperIndicator,
  StepperItem,
  StepperTitle,
  StepperTrigger,
};
